/**
 * タスクの終了状態バッジ（管理アプリ・task-end-reason-audit）
 *
 * タスクが途中で途切れているときだけ、その理由をバッジにする。
 * 正常完了と、記録のない完了済みタスク（本機能の導入前のもの）は何も出さない。
 */
import { deriveTaskEndState, type TaskEndState, type TurnEndRecord } from '@kyumo/common';
import {
  getTaskEndStateLabel,
  getTaskEndStateTone,
  getTurnErrorCodeLabel,
  TASK_END_TONE_CLASSES,
} from './turn-end-labels';

export interface TaskEndStateBadgeProps {
  status?: string | undefined;
  /** ISO 文字列または epoch ミリ秒 */
  updatedAt: string | number;
  lastTurnEnd?: TurnEndRecord | undefined;
  /** 現在時刻（テスト用）。省略時は Date.now() */
  now?: number | undefined;
  className?: string | undefined;
}

export function TaskEndStateBadge({
  status,
  updatedAt,
  lastTurnEnd,
  now,
  className,
}: TaskEndStateBadgeProps) {
  const state = deriveTaskEndState({ status, updatedAt, lastTurnEnd }, now ?? Date.now());
  if (!isCutOff(state, status)) return null;

  const tone = getTaskEndStateTone(state);
  const label = getTaskEndStateLabel(state);
  const errorCode = state.kind === 'ended' ? state.record.error?.code : undefined;

  return (
    <span
      className={`inline-flex flex-wrap items-center px-2 py-1 text-xs rounded ${TASK_END_TONE_CLASSES[tone]} ${className ?? ''}`}
      data-testid="task-end-state"
      data-state={state.kind}
      data-tone={tone}
    >
      {label}
      {errorCode && <span>：{getTurnErrorCodeLabel(errorCode)}</span>}
    </span>
  );
}

/**
 * 途切れているかどうか。記録のない終了は、状態が中断・エラーのときだけ途切れとみなす
 * （状態が完了または不明の記録なしタスクは導入前のもので、出しても情報がない）
 */
function isCutOff(state: TaskEndState, status: string | undefined): boolean {
  if (state.kind === 'ended') return state.record.reason !== 'completed';
  if (state.kind === 'unrecorded') return status === 'interrupted' || status === 'error';
  return true;
}
